import {sweetAPI} from 'app/store/sweet-api';
import {
  handleAccountLimitErrors,
  providesTagsByURL,
  showLoadingBar,
  showMessages,
  UPGRADE_MODALS,
} from 'app/store/sweet-api/utils';
import {TEAMTAG} from 'app/teams/api';
import {SCIMTEAM_API_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import type {ListResponseType, SCIMTeamSerializedType} from 'sweet-types';

export const SCIM_TAGS = {
  TEAM: 'scimteams',
};

export const extendedAPI = sweetAPI
  .enhanceEndpoints({
    addTagTypes: [SCIM_TAGS.TEAM, TEAMTAG],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getSCIMTeams: build.query<ListResponseType<SCIMTeamSerializedType>, void>(
        {
          query: () => ({
            url: SCIMTEAM_API_URL,
          }),
          onQueryStarted: showLoadingBar,
          providesTags: providesTagsByURL(SCIM_TAGS.TEAM),
        }
      ),

      createSCIMTeam: build.mutation<
        SCIMTeamSerializedType,
        {
          team: string;
          scim_external_id: string;
        }
      >({
        query: (body) => ({
          url: SCIMTEAM_API_URL,
          method: 'POST',
          body,
        }),
        onQueryStarted: (...args) => {
          showMessages({
            onSuccess: () => gt('Successfully associated SCIM external ID'),
            onError: {
              fallback: (response) =>
                response?.message ?? gt('Unable to associate SCIM external ID'),
              byStatus: {
                400: (response) =>
                  response?.[0] ??
                  response?.non_field_errors ??
                  response?.scim_external_id ??
                  gt('Unable to associate SCIM external ID'),
              },
            },
          })(...args);

          handleAccountLimitErrors(UPGRADE_MODALS.TEAM_LIMIT)(...args);
        },
        invalidatesTags: () => [
          {type: TEAMTAG, id: 'list'},
          {type: SCIM_TAGS.TEAM, id: 'list'},
        ],
      }),

      updateSCIMTeam: build.mutation<
        SCIMTeamSerializedType,
        {
          url: string;
          body: {
            scim_external_id: string;
            team: string;
          };
        }
      >({
        query: ({url, body}) => ({
          url: url,
          method: 'PATCH',
          body,
        }),
        onQueryStarted: showMessages({
          onError: {
            fallback: (response) =>
              response?.message ?? gt('Unable to associate SCIM external ID'),
            byStatus: {
              400: (response) =>
                response?.[0] ??
                response?.non_field_errors ??
                response?.scim_external_id ??
                gt('Unable to associate SCIM external ID'),
            },
          },
        }),
        invalidatesTags: (response) => [
          {type: TEAMTAG, id: 'list'},
          {type: SCIM_TAGS.TEAM, id: 'list'},
          ...(response != null
            ? [
                {type: SCIM_TAGS.TEAM, id: response.url} as {
                  type: typeof SCIM_TAGS.TEAM;
                  id: string;
                },
              ]
            : []),
        ],
      }),

      deleteSCIMTeam: build.mutation<unknown, {url: string; name?: string}>({
        query: ({url}) => ({
          url: url,
          method: 'DELETE',
        }),
        onQueryStarted: showMessages({
          onSuccess: ({name}) =>
            gt('Removed SCIM association for %s', name || ''),
          onError: () =>
            gt('Unable to remove the SCIM association at the moment'),
        }),
        invalidatesTags: () => [
          {type: TEAMTAG, id: 'list'},
          {type: SCIM_TAGS.TEAM, id: 'list'},
        ],
      }),
    }),
  });

export const {
  useGetSCIMTeamsQuery: useSCIMTeams,
  useCreateSCIMTeamMutation: useCreateSCIMTeam,
  useUpdateSCIMTeamMutation: useUpdateSCIMTeam,
  useDeleteSCIMTeamMutation: useDeleteSCIMTeam,
} = extendedAPI;
